/** Wholesale reads — rec_wholesale, READ-ONLY. The SELECT policy shipped in
 * MIGRATION_CRM2_PHASE4_READS.sql (Tom ran it 2026-07-16 AM). Same honest-
 * empty discipline as deals: RLS-closed tables return 200+[], so an empty
 * list always explains both possibilities.
 *
 * Row shape (MIGRATION_PHASE3.sql wholesale section): `data` = v1's whole
 * record {a, sub, seller, stage, ucp, buyer, fee, status, …}; `fields` = its
 * edits map (wl_* / wm_* keys). Stage is a STRING (WT_STAGES), not an index
 * like deals. Editing waits for a PHASE7 whitelist — not built yet. */
import { idbGet, idbSet } from "../../lib/idb";
import { pageAll } from "../edits/api";

/** v1's wholesale pipeline (wholesale.js WT_STAGES, verbatim order). */
export const WT_STAGES = [
  "New Lead", "Contacted", "Appointment Set", "Offer Made", "Negotiation",
  "Under Contract", "Disposition", "Assigned", "Closed", "Dead",
];

export interface WholesaleDeal {
  natKey: string;
  address: string;
  updatedAt: number;
  asking: string | null; // display string from v1 (wl_asking ?? wm_ask)
  stage: string | null; //   v1 w.stage — a WT_STAGES string
  seller: string | null;
  ucp: string | null; //     Under Contract Price (display string)
  buyer: string | null; //   Buyer Purchase Price
  fee: string | null; //     Assignment Fee
  status: string | null;
  fields: Record<string, unknown>;
  // Dispo sub-records (v1 dispo-modals.js). Stored on the record's data under
  // these keys; the wholesale door REPLACES an array key wholesale (rec_jsonb_merge
  // recurses only for objects), so whole-array saves are safe on the live table.
  offers: DispoOffer[];
  inquiries: DispoInquiry[];
  interested: DispoBuyer[];
  showings: DispoShowing[];
  // v1 wholesale.js/dispo-modals.js per-deal collab data (all on the record's data)
  contacts: WtContact[]; // w.contacts=[{n,r,c}]
  team: WtTeam[]; //         w.team=[{n,role}]
  chat: WtChat[]; //         w.chat=[{who,m}]
  comms: WtComm[]; //        w.comms=[{ic,t,m,tm,c}]
  notes: string; //          w.notes (string)
  flyer: WtFlyer; //         w.flyer (dispo marketing flyer)
  tasks: WtTask[]; //        w.tasks (Tasks & Appointments tab)
  files: WtFile[]; //        w.files (Files tab — legacy app list)
  link: string; //           w.link (public deal link)
}

export type DispoKind = "offers" | "inquiries" | "interested" | "showings";

/** v1 dispo flyer (wholesale.js seed w.flyer + dispoPanel fields). The persisted
 * core is headline/drive/contact-card; v2 also persists the editable property
 * facts so the flyer text is self-contained (v1 left those as placeholders). */
export interface WtFlyer {
  headline: string;
  drive: string; // photos Drive link
  contactName: string;
  company: string;
  phone: string;
  email: string;
  beds: string;
  baths: string;
  sqft: string;
  arv: string;
  year: string;
  notes: string; // condition notes
}

/** v1 wholesale collab record shapes (dispo-modals.js), byte-faithful. */
export interface WtContact { n: string; r: string; c: string; } // name, role, color
export interface WtTeam { n: string; role: string; }
export interface WtTask { t: string; done: boolean; due: string; who: string; } // v1 w.tasks
export interface WtFile { n: string; s: string; } // v1 w.files: name + source ('local'/url)
export interface WtChat { who: string; m: string; }
export interface WtComm { ic: string; t: string; m: string; tm: string; c: string; } // icon-key, type, message, time-str, color

/** Whole-array wholesale keys editable from v2 (arrays replace server-side). */
export type WtListKey = "contacts" | "team" | "chat" | "comms" | "tasks" | "files";

/** v1 addWtContact role menu + its color map (cmap). */
export const WT_CONTACT_ROLES = ["Seller", "Cash Buyer", "Title Company", "Agent", "Contractor", "Contact"];
export function wtContactColor(role: string): string {
  return role === "Cash Buyer" ? "emerald" : role === "Seller" ? "violet" : role === "Title Company" ? "blue" : role === "Agent" ? "amber" : "slate";
}

/** v1 logComm map: Call→phone/emerald, Text→message-square/blue, Email→mail/slate. */
export const WT_COMM_TYPES = ["Call", "Text", "Email"] as const;
export type WtCommType = (typeof WT_COMM_TYPES)[number];
export function wtCommMeta(type: WtCommType): { ic: string; c: string; emoji: string } {
  if (type === "Call") return { ic: "phone", c: "emerald", emoji: "📞" };
  if (type === "Text") return { ic: "message-square", c: "blue", emoji: "💬" };
  return { ic: "mail", c: "slate", emoji: "✉️" };
}
/** icon-key → emoji for rendering stored comms (v1 stores ic as a lucide name). */
export function commEmoji(ic: string): string {
  return ic === "phone" ? "📞" : ic === "message-square" ? "💬" : ic === "mail" ? "✉️" : "•";
}

export interface DispoOffer { id: string; buyer: string; co: string; ph: string; em: string; amt: number; emd: number; fin: string; close: string; status: string; notes: string; }
export interface DispoInquiry { id: string; buyer: string; co: string; ph: string; em: string; src: string; status: string; msg: string; }
export interface DispoBuyer { id: string; buyer: string; co: string; ph: string; em: string; level: string; budget: number; follow: string; notes: string; }
export interface DispoShowing { id: string; buyer: string; ph: string; dt: string; status: string; access: string; feedback: string; }

const dstr = (v: unknown): string => (typeof v === "string" ? v : v == null ? "" : String(v));
const dnum = (v: unknown): number => { const n = typeof v === "number" ? v : parseFloat(String(v ?? "").replace(/[^0-9.-]/g, "")); return Number.isFinite(n) ? n : 0; };
let _did = 0;
export const dispoId = (): string => "dsp" + Date.now().toString(36) + (_did++).toString(36);

function narrowRec(kind: DispoKind, raw: unknown): DispoOffer | DispoInquiry | DispoBuyer | DispoShowing {
  const o = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const id = dstr(o["id"]) || dispoId();
  if (kind === "offers") return { id, buyer: dstr(o["buyer"]), co: dstr(o["co"]), ph: dstr(o["ph"]), em: dstr(o["em"]), amt: dnum(o["amt"]), emd: dnum(o["emd"]), fin: dstr(o["fin"]), close: dstr(o["close"]), status: dstr(o["status"]) || "Submitted", notes: dstr(o["notes"]) };
  if (kind === "inquiries") return { id, buyer: dstr(o["buyer"]), co: dstr(o["co"]), ph: dstr(o["ph"]), em: dstr(o["em"]), src: dstr(o["src"]) || "Other", status: dstr(o["status"]) || "New", msg: dstr(o["msg"]) };
  if (kind === "interested") return { id, buyer: dstr(o["buyer"]), co: dstr(o["co"]), ph: dstr(o["ph"]), em: dstr(o["em"]), level: dstr(o["level"]) || "Warm", budget: dnum(o["budget"]), follow: dstr(o["follow"]), notes: dstr(o["notes"]) };
  return { id, buyer: dstr(o["buyer"]), ph: dstr(o["ph"]), dt: dstr(o["dt"]), status: dstr(o["status"]) || "Scheduled", access: dstr(o["access"]), feedback: dstr(o["feedback"]) };
}
function narrowList<T>(kind: DispoKind, v: unknown): T[] {
  return (Array.isArray(v) ? v : []).map((r) => narrowRec(kind, r)) as unknown as T[];
}

function narrowWtContacts(v: unknown): WtContact[] {
  if (!Array.isArray(v)) return [];
  const out: WtContact[] = [];
  for (const e of v) {
    if (!e || typeof e !== "object" || Array.isArray(e)) continue;
    const o = e as Record<string, unknown>;
    const n = dstr(o["n"]).trim();
    if (!n) continue;
    const r = dstr(o["r"]).trim() || "Contact";
    out.push({ n, r, c: dstr(o["c"]).trim() || wtContactColor(r) });
  }
  return out;
}
function narrowWtTeam(v: unknown): WtTeam[] {
  if (!Array.isArray(v)) return [];
  const out: WtTeam[] = [];
  for (const e of v) {
    if (!e || typeof e !== "object" || Array.isArray(e)) continue;
    const o = e as Record<string, unknown>;
    const n = dstr(o["n"]).trim();
    if (!n) continue;
    out.push({ n, role: dstr(o["role"]).trim() || "Member" });
  }
  return out;
}
/** v1 w.tasks = [{t, done(0/1), due, who}] — task/appointment rows on a deal. */
export function narrowWtTasks(v: unknown): WtTask[] {
  if (!Array.isArray(v)) return [];
  const out: WtTask[] = [];
  for (const e of v) {
    if (!e || typeof e !== "object" || Array.isArray(e)) continue;
    const o = e as Record<string, unknown>;
    const t = dstr(o["t"]).trim();
    if (!t) continue;
    out.push({ t, done: o["done"] === true || o["done"] === 1, due: dstr(o["due"]).trim(), who: dstr(o["who"]).trim() });
  }
  return out;
}
/** v1 w.files = [{n, s}] — the legacy app-only attachment list (name + source);
 * real Drive sync is a separate engine feature. */
export function narrowWtFiles(v: unknown): WtFile[] {
  if (!Array.isArray(v)) return [];
  const out: WtFile[] = [];
  for (const e of v) {
    if (!e || typeof e !== "object" || Array.isArray(e)) continue;
    const o = e as Record<string, unknown>;
    const n = dstr(o["n"]).trim();
    if (!n) continue;
    out.push({ n, s: dstr(o["s"]).trim() || "local" });
  }
  return out;
}

function narrowWtChat(v: unknown): WtChat[] {
  if (!Array.isArray(v)) return [];
  const out: WtChat[] = [];
  for (const e of v) {
    if (!e || typeof e !== "object" || Array.isArray(e)) continue;
    const o = e as Record<string, unknown>;
    const m = dstr(o["m"]).trim();
    if (!m) continue;
    out.push({ who: dstr(o["who"]).trim() || "?", m });
  }
  return out;
}
/** v1 seed: headline 'Off Market Hidden Gem | Flip or Rental', company
 * 'ETO Property Group', email houseflipsbytom@gmail.com, contactName 'Tom'. */
export function narrowWtFlyer(raw: unknown): WtFlyer {
  const o = raw && typeof raw === "object" && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  const g = (k: string, dflt = "") => (typeof o[k] === "string" ? (o[k] as string) : dflt);
  return {
    headline: g("headline", "Off Market Hidden Gem | Flip or Rental"),
    drive: g("drive"),
    contactName: g("contactName", "Tom"),
    company: g("company", "ETO Property Group"),
    phone: g("phone"),
    email: g("email", "houseflipsbytom@gmail.com"),
    beds: g("beds"),
    baths: g("baths"),
    sqft: g("sqft"),
    arv: g("arv"),
    year: g("year"),
    notes: g("notes"),
  };
}

function narrowWtComms(v: unknown): WtComm[] {
  if (!Array.isArray(v)) return [];
  const out: WtComm[] = [];
  for (const e of v) {
    if (!e || typeof e !== "object" || Array.isArray(e)) continue;
    const o = e as Record<string, unknown>;
    const m = dstr(o["m"]).trim();
    if (!m) continue;
    out.push({ ic: dstr(o["ic"]) || "message-square", t: dstr(o["t"]) || "Note", m, tm: dstr(o["tm"]) || "", c: dstr(o["c"]) || "slate" });
  }
  return out;
}

const CACHE = "wholesale_v1";

const s = (v: unknown): string | null => (typeof v === "string" && v.trim() ? v.trim() : null);
/** v1 stores these as display strings but "—" means empty. */
const disp = (v: unknown): string | null => {
  const x = s(v);
  return x === "—" ? null : x;
};

export function narrowWholesale(row: Record<string, unknown>): WholesaleDeal | null {
  const nk = s(row["nat_key"]);
  if (!nk) return null;
  const updated = s(row["updated_at"]);
  const t = updated ? Date.parse(updated) : NaN;
  const f = row["fields"];
  const fields0 = f !== null && typeof f === "object" && !Array.isArray(f) ? (f as Record<string, unknown>) : {};
  const d = row["data"];
  const data = d !== null && typeof d === "object" && !Array.isArray(d) ? (d as Record<string, unknown>) : {};
  // wl_*/wm_* live in the migrated `fields` column, but v2 edits ride the
  // wholesale door which merges into `data` (same path as stage/ucp/dispo). So a
  // v2 edit lands in data — we overlay data's wl_*/wm_* over the migrated fields
  // so a fresh edit wins while never-edited keys still show their migrated value.
  const fields: Record<string, unknown> = { ...fields0 };
  for (const [k, v] of Object.entries(data)) if (WF_KEY.test(k)) fields[k] = v;
  return {
    natKey: nk,
    address: s(row["address"]) ?? s(data["a"]) ?? nk,
    updatedAt: Number.isNaN(t) ? 0 : t,
    asking: s(fields["wl_asking"]) ?? s(fields["wm_ask"]),
    stage: s(data["stage"]),
    seller: disp(data["seller"]),
    ucp: disp(data["ucp"]),
    buyer: disp(data["buyer"]),
    fee: disp(data["fee"]),
    status: s(data["status"]),
    fields,
    offers: narrowList<DispoOffer>("offers", data["offers"]),
    inquiries: narrowList<DispoInquiry>("inquiries", data["inquiries"]),
    interested: narrowList<DispoBuyer>("interested", data["interested"]),
    showings: narrowList<DispoShowing>("showings", data["showings"]),
    contacts: narrowWtContacts(data["contacts"]),
    team: narrowWtTeam(data["team"]),
    chat: narrowWtChat(data["chat"]),
    comms: narrowWtComms(data["comms"]),
    notes: typeof data["notes"] === "string" ? (data["notes"] as string) : "",
    flyer: narrowWtFlyer(data["flyer"]),
    tasks: narrowWtTasks(data["tasks"]),
    files: narrowWtFiles(data["files"]),
    link: typeof data["link"] === "string" ? (data["link"] as string) : "",
  };
}

export async function cachedWholesale(): Promise<WholesaleDeal[] | null> {
  const c = await idbGet<{ at: number; rows: WholesaleDeal[] }>(CACHE);
  return c && Array.isArray(c.rows) ? c.rows : null;
}

export async function fetchWholesale(token: string, signal?: AbortSignal): Promise<{ rows: WholesaleDeal[]; dropped: number }> {
  // paged: PostgREST clamps limit to 1000 — a single shot silently truncates
  const rows = await pageAll<unknown>(token, "rec_wholesale?select=nat_key,address,fields,data,updated_at&deleted_at=is.null&order=updated_at.desc", signal);
  let dropped = 0;
  const out: WholesaleDeal[] = [];
  for (const r of Array.isArray(rows) ? rows : []) {
    const w = narrowWholesale(r as Record<string, unknown>);
    if (!w) {
      dropped++;
      continue;
    }
    out.push(w);
  }
  await idbSet(CACHE, { at: Date.now(), rows: out });
  return { rows: out, dropped };
}

// ---- Phase 9: editing (v1 setWtStage + deal-numbers fields + wtCalc) --------

/** v1 WT_STAGE_COL hues → v2 class suffixes (border accents per stage). */
export const WT_STAGE_HUES: Record<string, string> = {
  "New Lead": "slate", "Contacted": "sky", "Appointment Set": "indigo",
  "Offer Made": "amber", "Negotiation": "amber", "Under Contract": "violet",
  "Disposition": "blue", "Assigned": "emerald", "Closed": "emerald", "Dead": "red",
};

/** Patch the wholesale record's DATA (stage is a WT_STAGES string in data —
 * unlike deals there is no op-level stage; rec_apply_ops_core deep-merges). */
export function opEditWholesale(
  natKey: string,
  patch: Partial<{ stage: string; ucp: string; buyer: string; fee: string; seller: string; status: string; notes: string; link: string }>,
): import("../../lib/outbox").RecOp {
  return { s: "wholesale", op: "patch", k: natKey, d: patch as Record<string, unknown> };
}

/** Wholesale lead/listing field keys (wl_ / wm_ prefixed). Matches keys we overlay
 * from `data` onto `fields` at narrow time and write back through the wholesale door. */
export const WF_KEY = /^(wl_|wm_)[a-z]+$/i;

/** Field type for the wholesale Lead-Details / Listing clusters. */
export interface WfFieldDef { key: string; label: string; money?: boolean; ph: string }

/** v1 dispo-modals.js renderDispoTab — Lead Details + Property Snapshot (tab 1). */
export const WL_LEAD_FIELDS: WfFieldDef[] = [
  { key: "wl_source", label: "Source", ph: "Cold Call" },
  { key: "wl_motiv", label: "Motivation", ph: "Relocating" },
  { key: "wl_asking", label: "Asking", money: true, ph: "$175,000" },
  { key: "wl_timeline", label: "Timeline", ph: "30 days" },
  { key: "wl_mortgage", label: "Mortgage Balance", money: true, ph: "$92,000" },
];
export const WL_SNAPSHOT_FIELDS: WfFieldDef[] = [
  { key: "wl_bb", label: "Beds / Baths", ph: "3 / 2" },
  { key: "wl_sqft", label: "Sqft", ph: "1,360" },
  { key: "wl_yr", label: "Year Built", ph: "1992" },
  { key: "wl_occ", label: "Occupancy", ph: "Vacant" },
  { key: "wl_cond", label: "Condition", ph: "Needs cosmetic" },
  { key: "wl_access", label: "Access", ph: "Lockbox 1374" },
];
/** v1 dispo-modals.js renderDispoTab — Listing / Marketing (tab 4). */
export const WM_LISTING_FIELDS: WfFieldDef[] = [
  { key: "wm_mls", label: "On MLS", ph: "No" },
  { key: "wm_ask", label: "Asking to Buyers", money: true, ph: "$172,000" },
  { key: "wm_status", label: "Marketing Status", ph: "Active" },
  { key: "wm_photos", label: "Photos", ph: "12" },
  { key: "wm_listed", label: "Listed to Buyers", ph: "Jun 20, 2026" },
  { key: "wm_days", label: "Days Marketed", ph: "7" },
];

/** Read a wholesale field's current display string from the (merged) fields map. */
export function wfValue(fields: Record<string, unknown>, key: string): string {
  const v = fields[key];
  return typeof v === "string" ? v : v == null ? "" : String(v);
}

/** Patch a single wl_ / wm_ field through the wholesale door (merges into data;
 * narrowWholesale overlays it back over the migrated `fields`). Rejects unknown
 * keys so a typo can't write junk into the shared record. */
export function opEditWholesaleField(natKey: string, key: string, value: string): import("../../lib/outbox").RecOp {
  if (!WF_KEY.test(key)) throw new Error(`opEditWholesaleField: refusing non-wl_/wm_ key ${key}`);
  return { s: "wholesale", op: "patch", k: natKey, d: { [key]: value } };
}

/** v1 public deal link base (wtInit: 'https://flipbase.app/PublicDeal?token='+rand). */
export const WT_LINK_BASE = "https://flipbase.app/PublicDeal?token=";

/** Derived Activity Log (v1 tab 12 synthesizes rows from the deal's current
 * state — it is NOT a timestamped audit trail). We derive the same shape from
 * real record data: stage, offers/top-offer, showings, interested, public link. */
export function wtActivity(w: WholesaleDeal): string[] {
  const rows: string[] = [];
  if (w.stage) rows.push(`Stage: ${w.stage}`);
  if (w.offers.length) {
    const top = w.offers.reduce((m, o) => (o.amt > m ? o.amt : m), 0);
    rows.push(`${w.offers.length} offer${w.offers.length === 1 ? "" : "s"} received${top ? ` — top $${Math.round(top).toLocaleString("en-US")}` : ""}`);
  }
  if (w.showings.length) rows.push(`${w.showings.length} showing${w.showings.length === 1 ? "" : "s"} scheduled`);
  if (w.interested.length) rows.push(`${w.interested.length} interested buyer${w.interested.length === 1 ? "" : "s"}`);
  if (w.inquiries.length) rows.push(`${w.inquiries.length} inquir${w.inquiries.length === 1 ? "y" : "ies"} logged`);
  if (w.link) rows.push("Public deal link created");
  return rows;
}

/** Highest offer amount across a deal's offers (v1 w.dispo.highestN). */
export function highestOffer(offers: { amt: number }[]): number {
  return offers.reduce((m, o) => (o.amt > m ? o.amt : m), 0);
}

/** Dispo overview stats strip (v1 dispo-modals tab 3 header). */
export function dispoStats(w: WholesaleDeal): { inquiries: number; offers: number; highest: number; showings: number } {
  return { inquiries: w.inquiries.length, offers: w.offers.length, highest: highestOffer(w.offers), showings: w.showings.length };
}

/** One matched buyer with its buy-box criteria + score. */
export interface BuyerMatch<T> { b: T; crit: [string, boolean][]; pct: number; }

/** Best-effort parse of a US address's city + 2-letter state, e.g.
 * "123 Main St, Daytona Beach, FL 32114" → { city: "Daytona Beach", state: "FL" }.
 * Returns nulls for pieces that aren't present so callers can treat "unknown"
 * as "don't disqualify". */
export function dealCityState(address: string): { city: string | null; state: string | null } {
  const parts = address.split(",").map((s) => s.trim()).filter(Boolean);
  const last = parts[parts.length - 1] ?? "";
  const m = last.match(/\b([A-Za-z]{2})\b(?:\s+\d{5}(?:-\d{4})?)?\s*$/);
  const state = m && m[1] ? m[1].toUpperCase() : null;
  // when the last segment is the "ST ZIP" tail, the city is the segment before it;
  // when there's no state tail, fall back to the last segment as the city.
  const city = state && parts.length >= 2 ? parts[parts.length - 2] : parts[parts.length - 1];
  return { city: city ? city.trim() : null, state };
}

/** Score buyers against a deal's buy-box. Unlike v1 (which hardcoded State FL,
 * "Volusia County", and Property-type as always-pass — a demo stub), this scores
 * each buyer's REAL buy-box against the deal's parsed location + price:
 *   • State   — the buyer's state must match the deal's (a buyer with no state,
 *               or a deal with no parseable state, isn't disqualified).
 *   • Area    — the buyer's area/counties/cities free-text must mention the deal's
 *               city (empty buy-box, or an unknown deal city, passes — matching
 *               stays GENEROUS so we never hide a buyer on missing data).
 *   • Price   — the deal price sits in [min,max] (no cap, or a $0 deal, passes).
 * Sorted by match % desc. Generic so it needn't import the Buyer type. */
export function wtMatchBuyers<T extends { state: string; min: number; max: number; area?: string; counties?: string; cities?: string }>(
  buyers: T[],
  dealPrice: number,
  address = "",
): BuyerMatch<T>[] {
  const loc = dealCityState(address);
  const dealState = loc.state;
  const dealCity = (loc.city ?? "").toLowerCase();
  return buyers
    .map((b) => {
      const box = [b.area, b.counties, b.cities].filter(Boolean).join(" ").toLowerCase();
      const stateOk = !b.state || !dealState || b.state.toUpperCase() === dealState;
      const areaOk = !box || !dealCity || box.includes(dealCity);
      const priceOk = !b.max || (dealPrice >= b.min && dealPrice <= b.max) || dealPrice === 0;
      const crit: [string, boolean][] = [
        [dealState ? `State ${dealState}` : "State", stateOk],
        ["Buy-box area", areaOk],
        ["Price range", priceOk],
      ];
      const pass = crit.filter((c) => c[1]).length;
      return { b, crit, pct: Math.round((pass / crit.length) * 100) };
    })
    .sort((a, b) => b.pct - a.pct);
}

/** Build a deal "interested" row from a matched buyer (v1 addBuyerToDeal). */
export function buyerToInterested(
  b: { n: string; co: string; ph: string; em: string; max: number },
  id: string,
): DispoBuyer {
  return { id, buyer: b.n, co: b.co, ph: b.ph, em: b.em, level: "Warm", budget: b.max || 0, follow: "", notes: "Added from matched buyers" };
}

/** Whole-array replace of a collab list on a wholesale record (contacts / team /
 * chat / comms). Same door + semantics as opPutDispo — arrays replace wholesale
 * server-side, so passing the full updated list is safe. */
export function opPutWtList(natKey: string, key: WtListKey, list: unknown[]): import("../../lib/outbox").RecOp {
  return { s: "wholesale", op: "patch", k: natKey, d: { [key]: list } as Record<string, unknown> };
}

/** Whole-array replace of one dispo list on a wholesale record (v1 submitDispo
 * unshift → persistState). The wholesale door replaces an array key wholesale,
 * so passing the full updated list is safe (no server-side concat). */
export function opPutDispo(natKey: string, kind: DispoKind, list: unknown[]): import("../../lib/outbox").RecOp {
  return { s: "wholesale", op: "patch", k: natKey, d: { [kind]: list } as Record<string, unknown> };
}

/** Save the dispo flyer (object patch — deep-merges on the record's data). */
export function opSaveWtFlyer(natKey: string, flyer: WtFlyer): import("../../lib/outbox").RecOp {
  return { s: "wholesale", op: "patch", k: natKey, d: { flyer: flyer as unknown as Record<string, unknown> } };
}

const priceOf = (w: { buyer: string | null; ucp: string | null }): string => w.buyer || w.ucp || "—";

/** Copyable dispo flyer text (v1 Dispo Marketing Flyer). Pure — the UI just
 * hands it to the clipboard. */
export function buildFlyerText(w: { address: string; buyer: string | null; ucp: string | null }, f: WtFlyer): string {
  const facts = [
    [f.beds, "Bed"], [f.baths, "Bath"],
  ].filter(([v]) => v).map(([v, l]) => `${v} ${l}`).join(" / ");
  const line2 = [facts, f.sqft ? `${f.sqft} sqft` : "", f.year ? `Built ${f.year}` : ""].filter(Boolean).join(" · ");
  const contact = [f.contactName, f.company].filter(Boolean).join(", ");
  const reach = [f.phone, f.email].filter(Boolean).join(" · ");
  return [
    f.headline,
    "",
    w.address,
    line2,
    f.arv ? `ARV: ${f.arv}` : "",
    `Assignment Price: ${priceOf(w)}`,
    f.notes ? `\nCondition:\n${f.notes}` : "",
    "",
    contact ? `Contact: ${contact}` : "",
    reach,
    f.drive ? `Photos: ${f.drive}` : "",
  ].filter((l) => l !== "").join("\n");
}

/** The SMS campaign message (v1 dispoPanel composer). The flyer link is appended
 * separately by the caller; sending itself stays OFF (see SmsCampaign). */
export function buildSmsText(w: { address: string; buyer: string | null; ucp: string | null }, f: WtFlyer): string {
  const facts = [f.beds ? `${f.beds} Bed` : "", f.baths ? `${f.baths} Bath` : "", f.sqft ? `${f.sqft} sqft` : ""].filter(Boolean).join(" / ");
  return [
    w.address,
    facts,
    f.arv ? `ARV: ${f.arv}` : "",
    `Price: ${priceOf(w)}`,
    `More Details:${f.drive ? " " + f.drive : ""}`,
  ].filter(Boolean).join("\n");
}

/** v1 blastBuyers recipient set: Active + VIP cash buyers. */
export function activeBuyerCount<T extends { status: string }>(buyers: T[]): number {
  return buyers.filter((b) => b.status === "Active" || b.status === "VIP").length;
}

/** v1 money(): parse "$172,000" → 172000 (0 for junk, like v1). */
export const wtMoney = (v: string | number | null | undefined): number => {
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  const n = parseFloat(String(v ?? "").replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) ? n : 0;
};

/** v1 wtCalc (dispo-modals.js), verbatim: spread = buyer − ucp,
 * net = spread − costs, margin = buyer > 0 ? net/buyer×100 : 0. */
export function wtCalc(ucp: number, buyer: number, costs: number): { spread: number; net: number; margin: number } {
  const spread = buyer - ucp;
  const net = spread - costs;
  const margin = buyer > 0 ? (net / buyer) * 100 : 0;
  return { spread, net, margin };
}

/** v1's calculator seeds: card values when present, else 145000/172000/1500. */
export function wtCalcSeed(w: { ucp: string | null; buyer: string | null }): { ucp: number; buyer: number; costs: number } {
  return {
    ucp: wtMoney(w.ucp) || 145000,
    buyer: wtMoney(w.buyer) || 172000,
    costs: 1500,
  };
}
